import React, { useEffect } from 'react';
import DashboardOrderListFilter, {
  DASHBOARD_ORDER_FILTER_VALUES,
} from './components/DashboardOrderListFilter';
import { globalStyles } from 'snacks-design-system';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import DashboardOrderCard from '../DashboardOrderCard';
import style from './DashboardOrderList.module.scss';
import { IOrder } from '../../models/model-interface';
import { OrderPaymentStatus, OrderStatus } from '../../models/model-enum';
import { SORT_BY_STATUS } from '../../models/model-constants';
import DashboardEmptyFilteredOrderList from './components/DashboardEmptyFilteredOrderList';
import useDashboardStore from '../../hooks/use-dashboard-store';
import DashboardEmptyOrderList from '../DashboardEmptyOrderList';
import DashboardSideCardContentAutoFit from '../DashboardSideCardContentAutoFit';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { useApplicationUiStore } from '@src/shared/store/ui-store/application-ui-store';
import useDeliveryDashboadAnalytics from '../../tracking/use-delivery-dashboard-analytics';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';
import DashboardPostalCodeFilter from './components/DashboardPostalCodeFilter';
import PIEText from '@src/shared/components/PIEText';
import { useOrderListFilterValues } from './components/DashboardPostalCodeFilter/use-order-list-filter-values';
import { useTranslation } from 'react-i18next';
import { IconInfoCircle } from '@justeattakeaway/pie-icons-react';
import useBoundDashboardStore from '../../store/dashboardGlobalStore';
import dayjs from 'dayjs';
import { ManageDeliverySegmentedControlSelection } from '../DashboardManageDelivery';
import { isWithinPreOrderWindow } from '@src/shared/utils';
const { xdsSpacingE, xdsSpacingF, xdsSpacingD, xdsSpacingB, xdsSpacingC } = globalStyles;
interface componentProps {
  onOrderSelected: (order: IOrder) => void;
  togglerRefs: any;
}
export const DashboardOrderList = ({ onOrderSelected, togglerRefs }: componentProps) => {
  const {
    activeOrderListFilter,
    setActiveOrderListFilter,
    activePostCodeFilterValues,
    setActivePostCodeFilterValues,
    activeManageDeliveryTab,
  } = useDashboardUIStore();
  const { orderList } = useDashboardStore();
  const { appDomElementClientHeightRef } = useApplicationUiStore();
  const { trackOrderListView, trackFilteredOrderListView } = useDeliveryDashboadAnalytics();
  const { onboardingActive } = useOnboardingStore();
  const { getOrderListFilterItemByValue } = useOrderListFilterValues();
  const { getOrderEtaByUUID } = useBoundDashboardStore();
  const { t } = useTranslation();

  const isPreOrder = (order: IOrder) => !!order.isPreorder;

  useEffect(() => {
    if (
      activeOrderListFilter !== DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED ||
      activeManageDeliveryTab !== ManageDeliverySegmentedControlSelection.ORDERS
    ) {
      setActivePostCodeFilterValues([]);
    }
  }, [activeOrderListFilter, activeManageDeliveryTab]);

  const isPostCodeFilterVisible = () => {
    const postCodes = (orderList || [])
      .filter((o: IOrder) => o.status === OrderStatus.Unassigned)
      .map((o: IOrder) => o.customer.address.postcode);
    const uniquePostCodes = Array.from(new Set(postCodes));
    return uniquePostCodes.length > 1;
  };

  useEffect(() => {
    const visibility = isPostCodeFilterVisible();
    if (!visibility) {
      setActivePostCodeFilterValues([]);
    }
  }, [isPostCodeFilterVisible()]);

  const getComputedOrderList = () => {
    const FILTER_SCOPE = {
      [DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED]: [OrderStatus.Unassigned],
      [DASHBOARD_ORDER_FILTER_VALUES.ON_THE_WAY]: [
        OrderStatus.InDelivery,
        OrderStatus.Assigned,
        OrderStatus.AssignedToNextPool,
      ],
      [DASHBOARD_ORDER_FILTER_VALUES.DELIVERED]: [OrderStatus.Delivered],
      [DASHBOARD_ORDER_FILTER_VALUES.ALL]: [
        OrderStatus.Unassigned,
        OrderStatus.InDelivery,
        OrderStatus.Assigned,
        OrderStatus.AssignedToNextPool,
        OrderStatus.Delivered,
      ],
    };

    const filterByPostCode = (order: IOrder) => {
      if (
        isPostCodeFilterVisible() &&
        activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED &&
        activePostCodeFilterValues.length > 0
      ) {
        return activePostCodeFilterValues.includes(order.customer.address.postcode);
      }

      return true;
    };

    const filterByStatus = (order: IOrder) => {
      const currentFilterScope = FILTER_SCOPE[activeOrderListFilter];
      return currentFilterScope.includes(order.status);
    };

    const sortOrders = (a: IOrder, b: IOrder) => {
      if (activeOrderListFilter === 'ALL') {
        return SORT_BY_STATUS[a.status] - SORT_BY_STATUS[b.status];
      }

      if (activeOrderListFilter === 'UNASSIGNED') {
        return dayjs(b.orderDate).valueOf() - dayjs(a.orderDate).valueOf();
      }
      if (activeOrderListFilter === 'ON_THE_WAY') {
        console.log('sortOrders ON_THE_WAY', dayjs(b.updateDate).valueOf());
        console.log('sortOrders ON_THE_WAY2', dayjs(a.updateDate).valueOf());
        return dayjs(a.updateDate).valueOf() - dayjs(b.updateDate).valueOf();
      }
      return b.timestamp - a.timestamp;
    };

    const filterByPreOrderWindow = (order: IOrder) =>
      isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder);

    if (!orderList) return [];

    if (!activeOrderListFilter) {
      return orderList.filter(filterByPreOrderWindow).filter(filterByPostCode);
    }

    return orderList
      .filter(filterByStatus)
      .filter(filterByPreOrderWindow)
      .sort(sortOrders)
      .filter(filterByPostCode);
  };

  const handlePostCodeFilterChange = (selectedPostCodes: Array<string>) => {
    setActivePostCodeFilterValues(selectedPostCodes);
  };

  // ANALYTICS
  useEffect(() => {
    if (orderList && !onboardingActive) {
      const orderCount = getComputedOrderList().length;
      trackOrderListView(activeOrderListFilter, orderCount)?.once();
    }
  }, [orderList]);

  useEffect(() => {
    if (orderList && activeOrderListFilter) {
      const orderCount = getComputedOrderList().length;
      trackFilteredOrderListView(activeOrderListFilter, orderCount);
    }
  }, [activeOrderListFilter]);
  // --------

  const orderCounts = (status: OrderStatus) => {
    return orderList && orderList.filter((order) => order.status === status).length;
  };

  return (
    <>
      {orderList && orderList.length > 0 ? (
        <PIEComponentUtil paddingTop={xdsSpacingF}>
          <DashboardOrderListFilter
            predefinedSelection={activeOrderListFilter}
            onChanged={(filterValue) => {
              setActiveOrderListFilter(filterValue);
            }}
          />
          {activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED &&
            isPostCodeFilterVisible() && (
              <>
                <DashboardPostalCodeFilter
                  onSelectedPostCodesChange={handlePostCodeFilterChange}
                  orderList={
                    orderList.filter((order: IOrder) => order.status === OrderStatus.Unassigned) ||
                    []
                  }
                  initialSelectedPostCodes={activePostCodeFilterValues}
                />
              </>
            )}
          {(activePostCodeFilterValues.length && isPostCodeFilterVisible() && (
            <PIEComponentUtil marginBottom={xdsSpacingD}>
              <PIEText
                text={
                  <>
                    <b>{getComputedOrderList().length}</b>{' '}
                    {getOrderListFilterItemByValue(activeOrderListFilter)?.title.toLowerCase()}{' '}
                    {t('order.postcode_filter_result_value')}
                    <b>{activePostCodeFilterValues.join(', ')}</b>
                  </>
                }
                color="var(--xds-color-content-default)"
                size="var(--xds-font-size-16)"
                fontWeight="var(--xds-font-weight-regular)"
              />
            </PIEComponentUtil>
          )) ||
            null}
          <DashboardSideCardContentAutoFit
            config={{
              documentNavigationHeaderHeight:
                appDomElementClientHeightRef.appDomElementNavigationHeader as number,
              sectionOffsetA: appDomElementClientHeightRef.domElementOffsetA as number,
              sectionOffsetB: appDomElementClientHeightRef.domElementOffsetB as number,
              sectionOffsetC: appDomElementClientHeightRef.domElementOffsetC as number,
              contentItemHeight: 148,
              customOffset: 50 + (isPostCodeFilterVisible() ? 100 : 0),
            }}
          >
            {(getComputedOrderList()?.length && (
              <div className={style.DashboardOrderList}>
                {activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.ON_THE_WAY ? (
                  <>
                    <PIEText
                      size={'m'}
                      text={
                        <>
                          <PIEComponentUtil marginTop={xdsSpacingB} marginBottom={xdsSpacingC}>
                            <PIEText
                              size="var(--xds-font-size-16)"
                              fontWeight="var(--xds-font-weight-bold)"
                              text={
                                <>
                                  <>
                                    {t('courier_status_ontheway.IN_DELIVERY')}
                                    {orderCounts(OrderStatus.InDelivery)
                                      ? `(${orderCounts(OrderStatus.InDelivery)})`
                                      : ''}
                                  </>
                                </>
                              }
                            />
                          </PIEComponentUtil>
                          {orderList.filter(
                            (order: IOrder) =>
                              order.status === OrderStatus.InDelivery &&
                              isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder)
                          ).length ? (
                            <>
                              {orderList
                                .filter(
                                  (order: IOrder) =>
                                    order.status === OrderStatus.InDelivery &&
                                    isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder)
                                )
                                .sort(
                                  (a, b) =>
                                    dayjs(b.updateDate).valueOf() - dayjs(a.updateDate).valueOf()
                                )
                                .map((order: IOrder) => {
                                  return (
                                    <PIEComponentUtil key={order.uuid} marginBottom={xdsSpacingD}>
                                      <DashboardOrderCard
                                        uuid={order.uuid}
                                        orderETA={getOrderEtaByUUID(order.uuid)}
                                        onClick={() => onOrderSelected(order)}
                                        updateDate={order.updateDate}
                                        orderStatus={order.status}
                                        courier={order?.courier ? order.courier : undefined}
                                        customerName={order.customer?.lastName}
                                        city={order.customer.address.city}
                                        postCode={order.customer.address.postcode}
                                        isPaid={order.payment?.status === OrderPaymentStatus.Paid}
                                        isContainAlcohol={order.hasAlcohol}
                                        address={`${order.customer.address.street} ${order.customer.address.streetNumber}`}
                                        dueTime={order.expectedDeliveryTime}
                                        isPreOrder={false}
                                      />
                                    </PIEComponentUtil>
                                  );
                                })}
                            </>
                          ) : (
                            <PIEComponentUtil marginBottom={xdsSpacingE}>
                              <PIEText
                                size={'var(--xds-font-size-14)'}
                                text={
                                  <div
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                  >
                                    <IconInfoCircle size={'xs'} />
                                    {t('courier_status_ontheway.ORDERS_BEING_DELIVERED')}
                                  </div>
                                }
                              />
                            </PIEComponentUtil>
                          )}
                          <PIEComponentUtil marginBottom={xdsSpacingB} marginTop={xdsSpacingE}>
                            <PIEText
                              size="var(--xds-font-size-16)"
                              fontWeight="var(--xds-font-weight-bold)"
                              text={
                                <>
                                  {t('courier_status_ontheway.ASSIGNED_ORDERS')}
                                  {orderCounts(OrderStatus.Assigned)
                                    ? `(${orderCounts(OrderStatus.Assigned)})`
                                    : ''}
                                </>
                              }
                            />
                          </PIEComponentUtil>
                          {orderList.filter(
                            (order: IOrder) =>
                              order.status === OrderStatus.Assigned &&
                              isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder)
                          ).length ? (
                            <>
                              {orderList
                                .filter(
                                  (order: IOrder) =>
                                    order.status === OrderStatus.Assigned &&
                                    isWithinPreOrderWindow(order.expectedDeliveryTime, order.isPreorder)
                                )
                                .sort(
                                  (a, b) =>
                                    dayjs(b.updateDate).valueOf() - dayjs(a.updateDate).valueOf()
                                )
                                .map((order: IOrder) => {
                                  return (
                                    <PIEComponentUtil key={order.uuid} marginBottom={xdsSpacingD}>
                                      <DashboardOrderCard
                                        uuid={order.uuid}
                                        orderETA={getOrderEtaByUUID(order.uuid)}
                                        onClick={() => onOrderSelected(order)}
                                        updateDate={order.updateDate}
                                        orderStatus={order.status}
                                        courier={order?.courier ? order.courier : undefined}
                                        customerName={order.customer?.lastName}
                                        city={order.customer.address.city}
                                        postCode={order.customer.address.postcode}
                                        isPaid={order.payment?.status === OrderPaymentStatus.Paid}
                                        isContainAlcohol={order.hasAlcohol}
                                        address={`${order.customer.address.street} ${order.customer.address.streetNumber}`}
                                        dueTime={order.expectedDeliveryTime}
                                        isPreOrder={false}
                                      />
                                    </PIEComponentUtil>
                                  );
                                })}
                            </>
                          ) : (
                            <PIEComponentUtil marginBottom={xdsSpacingE}>
                              <PIEText
                                size={'var(--xds-font-size-14)'}
                                text={
                                  <div
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                  >
                                    <IconInfoCircle size={'xs'} />
                                    {t('order.empty_filtered_order_list_indelivery_description')}
                                  </div>
                                }
                              />
                            </PIEComponentUtil>
                          )}
                        </>
                      }
                    ></PIEText>
                  </>
                ) : null}

                {activeOrderListFilter !== DASHBOARD_ORDER_FILTER_VALUES.ON_THE_WAY &&
                  getComputedOrderList()?.map((order: IOrder, index: number) => (
                    <PIEComponentUtil key={index} marginBottom={xdsSpacingD}>
                      <DashboardOrderCard
                        uuid={order.uuid}
                        orderETA={getOrderEtaByUUID(order.uuid)}
                        onClick={() => onOrderSelected(order)}
                        updateDate={order.updateDate}
                        orderStatus={order.status}
                        courier={order?.courier ? order.courier : undefined}
                        customerName={order.customer?.lastName}
                        city={order.customer.address.city}
                        postCode={order.customer.address.postcode}
                        isPaid={order.payment?.status === OrderPaymentStatus.Paid}
                        isContainAlcohol={order.hasAlcohol}
                        address={`${order.customer.address.street} ${order.customer.address.streetNumber}`}
                        dueTime={order.expectedDeliveryTime}
                        isPreOrder={activeOrderListFilter === DASHBOARD_ORDER_FILTER_VALUES.UNASSIGNED ? isPreOrder(order) : false}
                      />
                    </PIEComponentUtil>
                  ))}
              </div>
            )) ||
              (activeOrderListFilter && (
                <PIEComponentUtil paddingBottom={xdsSpacingE}>
                  <DashboardEmptyFilteredOrderList filterValue={activeOrderListFilter} />
                </PIEComponentUtil>
              ))}
          </DashboardSideCardContentAutoFit>
        </PIEComponentUtil>
      ) : (
        <DashboardEmptyOrderList />
      )}
    </>
  );
};

export default DashboardOrderList;
